import { useState } from "react";
import { toast } from "react-toastify";
import { NxtbnDrawer } from "..";
import useApi from "../../api";
import ActionButtons from "./actionButton";
import { TextBadge } from "../common";
import enumChoice, { getEnumLabel } from "../../enum";
import PaymentTable from "./paymentTable";
import OrderedLineItemsTable from "./ordredLineItemsTable";
import DeviceInfo from "./deviceInfo";
import { useQuery, gql, useMutation } from '@apollo/client';
import Swal from "sweetalert2";
import { aggresiveToastError } from "../common/utils";

const UPDATE_COMMENT = gql`
  mutation MyMutation($comment: String!, $id: ID!) {
    updateOrderComment(comment: $comment, id: $id) {
      success
    }
  }
`;

const GET_ORDER_DETAILS = gql`
  query GetOrderDetails($alias: UUID!) {
    order(alias: $alias) {
      alias
      authorizeStatus
      chargeStatus
      billingAddress {
        addressType
        city
        country
        email
        firstName
        id
        lastName
        phoneNumber
        postalCode
        streetAddress
      }
      comment
      createdAt
      currencyConversionRate
      dbId
      dueDate
      humanizeTotalPrice
      id
      isOverdue
      lastModified
      lineItems {
        dbId
        quantity
        humanizeTotalPrice
        humanizePricePerUnit
        variant {
          displayName
          humanizePrice
        }
      }
      note
      overchargedAmount
      paymentTerm
      preferredPaymentMethod
      paymentMethod
      reservationStatus
      status
      humanizeTotalShippingCost
      humanizeTotalPrice
      due
      humanizeTotalTax
      humanizeTotalDiscountedAmount
      humanizeTotalPaidAmount
      shippingAddress {
        addressType
        city
        country
        dbId
        email
        firstName
        id
        lastName
        phoneNumber
        postalCode
        streetAddress
      }
      user {
        firstName
        fullName
        dateJoined
        isActive
        isStaff
        isStoreAdmin
        lastName
        isSuperuser
        phoneNumber
        username
      }
    }
  }
`;

function OrderDetails({ isOpen, onClose, alias }: { isOpen: boolean; onClose: () => void; alias: string }) {
  const api = useApi();
  const [openPaymentHistory, setOpenPaymentHistory] = useState<boolean>(false);
  const [openDeviceInfo, setOpenDeviceInfo] = useState<boolean>(false);

  const { data, loading, error, refetch } = useQuery(GET_ORDER_DETAILS, {
    variables: { alias: alias },
    notifyOnNetworkStatusChange: true,
  });

  const [updateComment, { loading: mutationLoading, error: mutationError }] = useMutation(UPDATE_COMMENT);

  const retryReservation = () => {
    api.retryStockReservation(alias).then(() => {
      toast.success("Reservation retried successfully");
    }, () => {
      toast.error("Failed to retry reservation");
    });
  };

  const onCommentBlur = (e: any) => {
    updateComment({
      variables: {
        comment: e.target.value,
        id: data?.order?.dbId
      }
    }).then(() => {
      toast.success("Comment updated successfully");
    }, (error) => {
      toast.error("Failed to update comment");
    })
  };





  const markAsFullfilled = async () => {
    const result = await Swal.fire({
      title: "Are you sure you want to mark this order as fulfilled?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, mark as fulfilled!"
    });

    if (result.isConfirmed) {
      try {
        await api.markOrderAsFulfilled(alias);
        refetch();
        Swal.fire({
          title: "Fulfilled!",
          text: "The order has been marked as fulfilled.",
          icon: "success"
        });
      } catch (error) {
        aggresiveToastError(error);
        Swal.fire({
          title: "Error!",
          text: "Failed to mark the order as fulfilled.",
          icon: "error"
        });
      }
    }
  };

  return (
    <NxtbnDrawer size="lg" title="Order Details" isOpen={isOpen} onClose={onClose}>
      <div className="max-w-7xl mx-auto p-6 bg-white shadow-xl rounded-xl">
        {/* Action Buttons and Order Info */}
        <div className="mb-6 space-y-4">
          <ActionButtons refetch={refetch} data={data?.order} />
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3 text-sm text-gray-600 border-b pb-2">
            <span className="font-medium">Ordered At:</span> <span>{new Date(data?.order?.createdAt).toLocaleString() || 'N/A'}</span>
            <span className="hidden sm:inline">•</span>
            <span className="font-medium">Alias:</span> <span>{data?.order?.alias || 'N/A'}</span>            

            <button
              onClick={() => setOpenDeviceInfo(true)}
              className="text-blue-600 hover:underline flex items-center gap-2"
            >
              <span>Device Info</span>
            </button>
          </div>
        </div>


{/* Main Content Section */}
<div className="bg-gray-50 shadow-lg rounded-xl overflow-hidden">
  {/* Header Summary Bar */}
  <div className="bg-white border-b border-gray-200 p-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="flex flex-col">
        <span className="text-sm text-gray-500">Order Status</span>
        <div className="mt-1">
          <TextBadge 
            size="lg" 
            value={data?.order?.status} 
            label={getEnumLabel(enumChoice.orderStatus, data?.order?.status)}
          />
        </div>
      </div>
      <div className="flex flex-col">
        <span className="text-sm text-gray-500">Total Amount</span>
        <span className="text-xl font-semibold text-gray-900">{data?.order?.humanizeTotalPrice}</span>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end col-span-1 sm:col-span-2 lg:col-span-1">
        <button
          onClick={() => setOpenPaymentHistory(true)}
          className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition flex items-center justify-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 22c5.421 0 10-4.579 10-10S17.421 2 12 2 2 6.579 2 12s4.579 10 10 10zm-1-14h2v2h-2v-2zm0 4h2v6h-2v-6z"/>
          </svg>
          <span>Payment</span>
        </button>
        <button
          onClick={markAsFullfilled}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition flex items-center justify-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm-1 15-5-5 1.414-1.414L11 14.172l6.586-6.586L19 9l-8 8z"/>
          </svg>
          <span>Mark As Fulfilled</span>
        </button>
      </div>


    </div>
  </div>

  {/* Main Content Grid */}
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
    {/* Left Column - Financial Details */}
    <div className="lg:col-span-2 space-y-6">
      {/* Order Summary Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Financial Summary</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <span className="text-sm text-gray-500">Total Discounted</span>
              <p className="text-lg font-medium text-gray-900 mt-1">
                {data?.order?.humanizeTotalDiscountedAmount}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <span className="text-sm text-gray-500">Shipping Cost</span>
              <p className="text-lg font-medium text-gray-900 mt-1">
                {data?.order?.humanizeTotalShippingCost.toLocaleString()}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <span className="text-sm text-gray-500">Total Tax</span>
              <p className="text-lg font-medium text-gray-900 mt-1">
                {data?.order?.humanizeTotalTax.toLocaleString()}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <span className="text-sm text-gray-500">Due Amount</span>
              <p className="text-lg font-medium text-gray-900 mt-1">
                {data?.order?.due}
                {data?.order?.isOverdue && (
                  <span className="text-red-500 text-sm ml-2">(Overdue)</span>
                )}
              </p>
            </div>
          </div>

          {data?.order.charge_status === enumChoice.chargeStatus.toKV().OVERCHARGED.value && (
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <span className="text-sm text-yellow-800">Overcharged Amount</span>
              <p className="text-lg font-medium text-yellow-900 mt-1">
                {data?.order?.overchargedAmount.toLocaleString()}
              </p>
            </div>
          )}

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <span className="text-sm text-green-800">Total Paid Amount</span>
            <p className="text-lg font-medium text-green-900 mt-1">
              {data?.order?.humanizeTotalPaidAmount}
            </p>
          </div>
        </div>
      </div>

      {/* Address Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data?.order.shipping_address && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Shipping Address
            </h3>
            <div className="space-y-2">
              <p className="text-gray-600">{data?.order?.shipping_address?.street_address}</p>
              <p className="text-gray-600">
                {data?.order?.shipping_address?.city}, {data?.order?.shipping_address?.state} {data?.order?.shipping_address?.zip_code}
              </p>
              <p className="text-gray-600">{data?.order?.shipping_address?.country}</p>
            </div>
          </div>
        )}

        {data?.order.billing_address && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Billing Address
            </h3>
            <div className="space-y-2">
              <p className="text-gray-600">{data?.order?.billing_address?.street_address}</p>
              <p className="text-gray-600">
                {data?.order?.billing_address?.city}, {data?.order?.billing_address?.state} {data?.order?.billing_address?.zip_code}
              </p>
              <p className="text-gray-600">{data?.order?.billing_address?.country}</p>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Right Column - Order Details */}
    <div className="space-y-6">
      {/* Order Status Details */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Order Status</h3>
          <h3 className="text-gray-500">#{data?.order?.dbId}</h3>
        </div>
        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Stock Reservation</span>
              <TextBadge 
                size="sm" 
                value={data?.order?.reservationStatus} 
                label={getEnumLabel(enumChoice.orderStockReservationStatus, data?.order?.reservationStatus)} 
              />
            </div>
            {enumChoice.orderStockReservationStatus.toKV().FAILED.value === data?.order?.reservationStatus && (
              <button 
                onClick={retryReservation}
                className="text-red-600 text-sm hover:text-red-700 underline"
              >
                Retry Reservation
              </button>
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Charge Status</span>
            <TextBadge 
              size="sm" 
              value={data?.order?.chargeStatus} 
              label={getEnumLabel(enumChoice.chargeStatus, data?.order?.chargeStatus)} 
            />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Payment Term</span>
            <TextBadge 
              size="sm" 
              value={data?.order?.paymentTerm} 
              label={getEnumLabel(enumChoice.paymentTerms, data?.order?.paymentTerm)} 
            />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Payment Method</span>
            <TextBadge 
              size="sm" 
              value={data?.order?.paymentMethod} 
              label={getEnumLabel(enumChoice?.paymentMethods, data?.order?.paymentMethod)} 
            />
          </div>

          {data?.order.dueDate && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Due Date</span>
              <span className="text-sm text-gray-900">{data?.order?.dueDate}</span>
            </div>
          )}
        </div>
      </div>

      {/* Order Note */}
      {data?.order.note && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Note</h3>
          <p className="text-gray-600">{data?.order?.note}</p>
        </div>
      )}

      {/* Customer Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Info</h3>
        {data?.order?.user ? (
          <div className="space-y-2">
            <p className="text-gray-600">
              {data?.order?.user?.first_name || 'N/A'} {data?.order.user?.last_name || 'N/A'}
            </p>
            <p className="text-gray-600">{data?.order?.user?.email || 'N/A'}</p>
            <p className="text-gray-600">{data?.order?.user?.phone || 'N/A'}</p>
          </div>
        ) : (
          <p className="text-gray-600">This customer is a guest user.</p>
        )}
      </div>
    </div>
  </div>
</div>

















        {/* Full Width Sections */}
        <div className="space-y-6">
          {/* Line Items */}
          <OrderedLineItemsTable data={data?.order} />

          {/* Comment Section */}
          <div className="bg-gray-50 shadow-md rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Comment</h3>
            <textarea 
              onBlur={onCommentBlur} 
              name='comment' 
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" 
              placeholder="Add notes here.."
              defaultValue={data?.order?.comment || ''}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {openPaymentHistory && <PaymentTable alias={alias} isOpen={openPaymentHistory} onClose={() => setOpenPaymentHistory(false)} />}
      {openDeviceInfo && <DeviceInfo isOpen={openDeviceInfo} onClose={() => setOpenDeviceInfo(false)} alias={data?.order?.alias} />}
    </NxtbnDrawer>
  );
}

export default OrderDetails;